/**
 * AppICEMAP — CommunityDashboard
 * Custom element: `<community-dashboard>`
 *
 * Displays community-wide stats: today's activity, top categories,
 * heat by hour histogram.
 *
 * Props:
 *   stats { object } — from fetchCommunityStats()
 */

import { getCategoryStyle } from "../../utils/categoryStyle.js";
import iconCss from "../../../css/utilities/phosphor-shadow.css?inline";

export class CommunityDashboard extends HTMLElement {
	constructor() {
		super();
		this.attachShadow({ mode: "open" });
	}

	static get observedAttributes() {
		return ["loading"];
	}

	connectedCallback() {
		this._render();
	}

	attributeChangedCallback(name, _oldVal, _newVal) {
		if (name === "loading") {
			this._render();
		}
	}

	/** @param {object | null} val */
	set stats(val) {
		this._stats = val;
		this._render();
	}
	get stats() {
		return this._stats;
	}

	/* ── Render ──────────────────────────────────────────────────────────── */

	_render() {
		const isLoading = this.hasAttribute("loading");
		const stats = this._stats;

		this.shadowRoot.innerHTML = `
			<style>${iconCss}
				:host { display: block; }
				.cd { display: flex; flex-direction: column; gap: var(--space-4, 16px); }
				.cd--loading { opacity: 0.5; pointer-events: none; }

				/* Hero stat grid */
				.cd-hero {
					display: grid;
					grid-template-columns: repeat(2, 1fr);
					gap: var(--space-3, 12px);
				}
				.cd-hero__stat {
					display: flex; flex-direction: column; align-items: center;
					padding: var(--space-5, 20px) var(--space-4, 16px);
					background: var(--color-surface, #232532);
					border-radius: var(--radius-lg, 14px);
					box-shadow: var(--shadow-sm);
				}
				.cd-hero__stat:first-child {
					grid-column: 1 / -1;
					flex-direction: row;
					justify-content: center;
					gap: var(--space-4, 16px);
				}
				.cd-hero__num {
					font-size: var(--text-2xl, 2rem);
					font-weight: 700;
					color: var(--color-accent, #9184d9);
					line-height: var(--leading-2xl, 1.05);
				}
				.cd-hero__label {
					font-size: var(--text-xs, 0.6875rem);
					color: var(--color-neutral-400, #b2b6ca);
					text-align: center;
				}

				/* Category bars */
				.cd-categories {
					padding: var(--space-4, 16px);
					background: var(--color-surface, #232532);
					border-radius: var(--radius-lg, 14px);
					box-shadow: var(--shadow-sm);
				}
				.cd-categories__title {
					font-size: var(--text-xs, 0.6875rem);
					font-weight: 600;
					color: var(--color-neutral-400, #b2b6ca);
					text-transform: uppercase;
					letter-spacing: var(--tracking-uppercase, 0.08em);
					margin-bottom: var(--space-4, 16px);
				}
				.cd-cat-bar {
					display: flex; align-items: center; gap: var(--space-2, 8px);
					margin-bottom: var(--space-3, 12px);
				}
				.cd-cat-bar:last-child { margin-bottom: 0; }
				.cd-cat-bar__label {
					font-size: var(--text-xs, 0.6875rem);
					color: var(--color-text, #e9e9ed);
					width: 80px; flex-shrink: 0;
					overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
				}
				.cd-cat-bar__track {
					flex: 1; height: 10px;
					background: var(--color-neutral-800, #3f424d);
					border-radius: var(--radius-full, 9999px);
					overflow: hidden;
				}
				.cd-cat-bar__fill {
					height: 100%; border-radius: var(--radius-full, 9999px);
					background: var(--color-accent, #9184d9);
					transition: width var(--duration-normal, 250ms) var(--easing-standard);
				}
				.cd-cat-bar__count {
					font-size: var(--text-xs, 0.6875rem);
					color: var(--color-neutral-400, #b2b6ca);
					width: 32px; text-align: right; flex-shrink: 0;
					font-variant-numeric: tabular-nums;
				}

				/* Heat by hour */
				.cd-heat {
					padding: var(--space-4, 16px);
					background: var(--color-surface, #232532);
					border-radius: var(--radius-lg, 14px);
					box-shadow: var(--shadow-sm);
				}
				.cd-heat__title {
					font-size: var(--text-xs, 0.6875rem);
					font-weight: 600;
					color: var(--color-neutral-400, #b2b6ca);
					text-transform: uppercase;
					letter-spacing: var(--tracking-uppercase, 0.08em);
					margin-bottom: var(--space-3, 12px);
				}
				.cd-heat__grid {
					display: grid;
					grid-template-columns: repeat(12, 1fr);
					gap: 3px;
					height: 36px;
				}
				.cd-heat__cell {
					border-radius: 3px;
					background: color-mix(in srgb, var(--color-text) 7%, transparent);
					transition: background var(--duration-fast, 150ms);
				}
				/* Cuatro niveles de intensidad sobre el acento */
				.cd-heat__cell--l1 { background: color-mix(in srgb, var(--color-accent) 28%, transparent); }
				.cd-heat__cell--l2 { background: color-mix(in srgb, var(--color-accent) 50%, transparent); }
				.cd-heat__cell--l3 { background: color-mix(in srgb, var(--color-accent) 72%, transparent); }
				.cd-heat__cell--l4 { background: var(--color-accent, #9184d9); }
				/* La hora actual, marcada */
				.cd-heat__cell--now {
					outline: 1.5px solid var(--color-accent-700, #d2cefd);
					outline-offset: 1px;
				}

				/* Lectura en lenguaje natural de los datos */
				.cd-insight {
					display: flex;
					gap: var(--space-3, 12px);
					align-items: flex-start;
					padding: var(--space-3, 12px) var(--space-4, 16px);
					margin-bottom: var(--space-4, 16px);
					border-radius: var(--radius-md, 10px);
					background: color-mix(in srgb, var(--color-accent) 10%, transparent);
					border: 1px solid color-mix(in srgb, var(--color-accent) 22%, transparent);
				}
				.cd-insight i {
					font-size: 18px;
					color: var(--color-accent-2, #a7a1db);
					flex-shrink: 0;
					margin-top: 1px;
				}
				.cd-insight p {
					margin: 0;
					font-size: var(--text-sm, 13px);
					line-height: 1.5;
					color: var(--color-neutral-300, #cfd3e5);
				}
				.cd-insight strong { color: var(--color-text, #e9e9ed); font-weight: 600; }
				.cd-heat__labels {
					display: flex; justify-content: space-between;
					margin-top: var(--space-1, 4px);
					font-size: 9px; color: var(--color-neutral-500, #9397ab);
				}

				/* Skeleton */
				.cd-skeleton {
					background: var(--color-neutral-800, #3f424d);
					border-radius: var(--radius-md, 8px);
					animation: cd-pulse 1.5s ease-in-out infinite;
				}
				@keyframes cd-pulse {
					0%, 100% { opacity: 0.4; }
					50% { opacity: 0.7; }
				}

				/* Empty state */
				.cd-empty {
					text-align: center; padding: var(--space-8, 32px) var(--space-4, 16px);
					color: var(--color-neutral-500, #9397ab);
				}
				.cd-empty i { font-size: 2rem; display: block; margin-bottom: var(--space-2, 8px); }
				.cd-empty__text { font-size: var(--text-sm, 0.8125rem); }
			</style>

			<div class="cd ${isLoading ? "cd--loading" : ""}">
				${isLoading ? this._renderSkeleton() : this._renderContent(stats)}
			</div>
		`;
	}

	_renderSkeleton() {
		return `
			<div class="cd-skeleton" style="height:96px"></div>
			<div class="cd-skeleton" style="height:160px"></div>
			<div class="cd-skeleton" style="height:96px"></div>
		`;
	}

	_renderContent(stats) {
		if (!stats) {
			return `
				<div class="cd-empty">
					<i class="ph ph-chart-bar"></i>
					<div class="cd-empty__text">No hay estadísticas disponibles</div>
				</div>
			`;
		}

		const {
			totalReports,
			reportsToday,
			byCategory,
			topCategory,
			byHour,
			peakHour,
			totalContributors,
		} = stats;
		const maxCat = Math.max(1, ...(byCategory || []).map((c) => c.count));

		// Real activity per hour, computed from the reports themselves
		const heatCells = this._renderHeatCells(byHour);

		return `
			<div class="cd-hero">
				<div class="cd-hero__stat">
					<span class="cd-hero__num">${totalReports.toLocaleString("es")}</span>
					<span class="cd-hero__label">Reportes activos</span>
				</div>
				<div class="cd-hero__stat">
					<span class="cd-hero__num">${reportsToday}</span>
					<span class="cd-hero__label">Hoy</span>
				</div>
				<div class="cd-hero__stat">
					<span class="cd-hero__num">${totalContributors}</span>
					<span class="cd-hero__label">Colaboradores</span>
				</div>
			</div>

			${
				topCategory || peakHour !== null
					? `<div class="cd-insight">
				<i class="ph ph-lightbulb" aria-hidden="true"></i>
				<p>
					${
						topCategory
							? `Lo que más se reporta ahora mismo es <strong>${topCategory.label}</strong> (${topCategory.count} de ${totalReports}).`
							: ""
					}
					${
						peakHour !== null
							? ` La hora más movida es alrededor de las <strong>${String(peakHour).padStart(2, "0")}:00</strong>.`
							: ""
					}
				</p>
			</div>`
					: ""
			}

			<div class="cd-categories">
				<div class="cd-categories__title">Categorías activas</div>
				${(byCategory || [])
					.map((cat) => {
						const { color } = getCategoryStyle({ slug: cat.category });
						return `
					<div class="cd-cat-bar">
						<span class="cd-cat-bar__label">${cat.label}</span>
						<div class="cd-cat-bar__track">
							<div class="cd-cat-bar__fill" style="width:${Math.round((cat.count / maxCat) * 100)}%;background:${color}"></div>
						</div>
						<span class="cd-cat-bar__count">${cat.count}</span>
					</div>
				`;
					})
					.join("")}
				${(byCategory || []).length === 0 ? '<div class="cd-empty"><span class="cd-empty__text">Sin datos</span></div>' : ""}
			</div>

			<div class="cd-heat">
				<div class="cd-heat__title">Actividad por hora</div>
				<div class="cd-heat__grid">${heatCells}</div>
				<div class="cd-heat__labels">
					<span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span>
				</div>
			</div>
		`;
	}

	/**
	 * Activity-per-hour bars from real data.
	 * Previously this drew an invented bell curve, which looked plausible and
	 * told the user nothing true.
	 *
	 * @param {number[]} byHour - 24 counts, index = hour of day
	 * @returns {string}
	 */
	_renderHeatCells(byHour) {
		const hours = Array.isArray(byHour) && byHour.length === 24
			? byHour
			: new Array(24).fill(0);
		const max = Math.max(1, ...hours);
		const nowHour = new Date().getHours();

		return hours
			.map((count, hour) => {
				// 0 → apagado; el resto en 4 niveles de intensidad
				const level = count === 0 ? 0 : Math.ceil((count / max) * 4);
				const isNow = hour === nowHour ? " cd-heat__cell--now" : "";
				return `<div class="cd-heat__cell cd-heat__cell--l${level}${isNow}"
					title="${String(hour).padStart(2, "0")}:00 — ${count} reporte${count === 1 ? "" : "s"}"></div>`;
			})
			.join("");
	}
}

if (!customElements.get("community-dashboard")) {
	customElements.define("community-dashboard", CommunityDashboard);
}
